import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MobileStepper,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import type { User } from '@kangaroo/shared';

import { LoomEmbed } from './help/LoomEmbed';
import { getKnowledgeBaseArticlesByCategory } from '../lib/knowledgeBase';

const WIZARD_VERSION = 'v1';
const STORAGE_KEY_PREFIX = 'paddiq:onboarding-wizard-dismissed';

const STEPS = getKnowledgeBaseArticlesByCategory('getting-started')
  .filter((article) => article.loomId)
  .map((article) => ({
    title: article.title,
    loomId: article.loomId!,
  }));

function storageKey(userId: string) {
  return `${STORAGE_KEY_PREFIX}:${WIZARD_VERSION}:${userId}`;
}

function readDismissed(userId: string) {
  if (typeof window === 'undefined') {
    return true;
  }

  return window.localStorage.getItem(storageKey(userId)) === '1';
}

function writeDismissed(userId: string) {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(storageKey(userId), '1');
}

type OnboardingWizardDialogProps = {
  user: User | null;
  disabled?: boolean;
};

export function OnboardingWizardDialog({ user, disabled = false }: OnboardingWizardDialogProps) {
  const [open, setOpen] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const activeStep = STEPS[stepIndex];
  const userId = user?.id ?? null;

  useEffect(() => {
    if (!userId || disabled || readDismissed(userId) || STEPS.length === 0) {
      setOpen(false);
      return;
    }

    setStepIndex(0);
    setOpen(true);
  }, [disabled, userId]);

  if (!activeStep) {
    return null;
  }

  function dismiss() {
    if (userId) {
      writeDismissed(userId);
    }
    setOpen(false);
  }

  function nextStep() {
    if (stepIndex >= STEPS.length - 1) {
      dismiss();
      return;
    }

    setStepIndex((current) => Math.min(current + 1, STEPS.length - 1));
  }

  return (
    <Dialog
      open={open}
      onClose={dismiss}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          width: { xs: 'calc(100% - 24px)', md: 'min(1180px, calc(100% - 64px))' },
        },
      }}
    >
      <DialogTitle sx={{ pb: 1.25 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '1.12rem', fontWeight: 900, lineHeight: 1.2 }}>
              {activeStep.title}
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', mt: 0.25 }}>
              {stepIndex + 1} of {STEPS.length}
            </Typography>
          </Box>
          <IconButton aria-label="Dismiss onboarding" onClick={dismiss} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ pt: '8px !important' }}>
        <LoomEmbed loomId={activeStep.loomId} title={`Paddiq onboarding ${stepIndex + 1}`} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, alignItems: 'center', justifyContent: 'space-between' }}>
        <Button onClick={dismiss} sx={{ textTransform: 'none', fontWeight: 700 }}>
          Dismiss
        </Button>
        <Stack direction="row" spacing={1} alignItems="center">
          <MobileStepper
            variant="dots"
            steps={STEPS.length}
            position="static"
            activeStep={stepIndex}
            backButton={null}
            nextButton={null}
            sx={{ bgcolor: 'transparent', p: 0, mr: 0.5 }}
          />
          <Button
            variant="outlined"
            startIcon={<KeyboardArrowLeftIcon />}
            onClick={() => setStepIndex((current) => Math.max(current - 1, 0))}
            disabled={stepIndex === 0}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Back
          </Button>
          <Button
            variant="contained"
            endIcon={stepIndex >= STEPS.length - 1 ? undefined : <KeyboardArrowRightIcon />}
            onClick={nextStep}
            sx={{ textTransform: 'none', fontWeight: 800, minWidth: 96 }}
          >
            {stepIndex >= STEPS.length - 1 ? 'Done' : 'Next'}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
